import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Droplet, Plus, Play, Clock, CheckCircle2, User, Activity, Trash2, Edit } from 'lucide-react';
import InfusionFormDialog from '@/components/InfusionFormDialog';
import InfusionMonitor from '@/components/InfusionMonitor';
import { toast } from 'sonner';

const Infusions = () => {
  const [infusions, setInfusions] = useState([]);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingInfusion, setEditingInfusion] = useState(null);
  const [monitoringInfusion, setMonitoringInfusion] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [infusionsRes, patientsRes] = await Promise.all([
        axios.get(`${API}/infusions`),
        axios.get(`${API}/patients`)
      ]);

      setInfusions(infusionsRes.data);

      const patientsMap = {};
      patientsRes.data.forEach(p => {
        patientsMap[p.id] = p;
      });
      setPatients(patientsMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInfusionCreated = () => {
    fetchData();
    setShowDialog(false);
    setEditingInfusion(null);
  };

  const handleMonitorUpdate = () => {
    fetchData();
  };

  const handleEdit = (infusion, e) => {
    e.stopPropagation();
    setEditingInfusion(infusion);
    setShowDialog(true);
  };

  const handleDelete = async (infusionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('⚠️ SUPPRIMER DÉFINITIVEMENT cette perfusion ?\n\nCette action est irréversible. La perfusion sera complètement supprimée de la base de données.')) {
      return;
    }

    try {
      console.log('Deleting infusion:', infusionId);
      await axios.delete(`${API}/infusions/${infusionId}`);
      console.log('Delete successful, refreshing data...');
      toast.success('✅ Perfusion supprimée définitivement');
      await fetchData();
    } catch (error) {
      console.error('Error deleting infusion:', error);
      toast.error('❌ Erreur lors de la suppression de la perfusion');
    }
  };

  const getStatusColor = (statut) => {
    const colors = {
      planifie: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-yellow-100 text-yellow-700',
      termine: 'bg-green-100 text-green-700',
      annule: 'bg-red-100 text-red-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (statut) => {
    const labels = {
      planifie: 'Planifiée',
      en_cours: 'En cours',
      termine: 'Terminée',
      annule: 'Annulée'
    };
    return labels[statut] || statut;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const filterByStatus = (statut) => {
    return infusions.filter(inf => inf.statut === statut);
  };

  const InfusionCard = ({ infusion }) => {
    const patient = patients[infusion.patient_id];

    return (
      <Card
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setMonitoringInfusion(infusion)}
        data-testid={`infusion-${infusion.id}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-gray-900">{infusion.protocole.nom}</h3>
              {patient && (
                <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{patient.identite.prenom} {patient.identite.nom}</span>
                </div>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(infusion.statut)}`}>
              {getStatusLabel(infusion.statut)}
            </span>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between text-gray-600">
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Durée prévue
              </span>
              <span className="font-medium text-gray-900">{infusion.protocole.duree} min</span>
            </div>
            <div className="flex items-center justify-between text-gray-600">
              <span>Créée le</span>
              <span className="font-medium text-gray-900">{formatDate(infusion.created_at)}</span>
            </div>
            {infusion.start_time && (
              <div className="flex items-center justify-between text-gray-600">
                <span>Démarrée</span>
                <span className="font-medium text-gray-900">{formatDate(infusion.start_time)}</span>
              </div>
            )}
            {infusion.end_time && (
              <div className="flex items-center justify-between text-gray-600">
                <span>Terminée</span>
                <span className="font-medium text-gray-900">{formatDate(infusion.end_time)}</span>
              </div>
            )}
          </div>

          <div className="space-y-2 mt-4">
            {infusion.statut !== 'termine' && (
              <>
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMonitoringInfusion(infusion);
                  }}
                >
                  {infusion.statut === 'planifie' ? (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Démarrer
                    </>
                  ) : (
                    <>
                      <Activity className="w-4 h-4 mr-2" />
                      Monitoring
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/infusions/monitor/${infusion.id}`, '_blank');
                  }}
                  data-testid={`open-new-tab-${infusion.id}`}
                >
                  Ouvrir dans un nouvel onglet
                </Button>
              </>
            )}
            
            {/* Actions disponibles pour toutes les perfusions */}
            <div className="flex space-x-2 mt-2">
              {infusion.statut === 'planifie' && (
                <Button
                  variant="outline"
                  className="flex-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                  onClick={(e) => handleEdit(infusion, e)}
                  title="✏️ Modifier la perfusion"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="outline"
                className="flex-1 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                onClick={(e) => handleDelete(infusion.id, e)}
                title="🗑️ SUPPRIMER DÉFINITIVEMENT (irréversible)"
                data-testid={`delete-infusion-${infusion.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const planifiees = filterByStatus('planifie');
  const enCours = filterByStatus('en_cours');
  const terminees = filterByStatus('termine');

  return (
    <div className="space-y-6" data-testid="infusions-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Perfusions IV</h1>
          <p className="text-gray-600 mt-1">Suivi des séances de vitaminothérapie intraveineuse</p>
        </div>
        <Button
          onClick={() => {
            setEditingInfusion(null);
            setShowDialog(true);
          }}
          data-testid="add-infusion-button"
          style={{
            background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Perfusion
        </Button>
      </div>

      {infusions.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-16 text-gray-500">
              <Droplet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Aucune perfusion planifiée</p>
              <p className="text-sm">Commencez par créer une nouvelle séance</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">Toutes ({infusions.length})</TabsTrigger>
            <TabsTrigger value="en_cours">En cours ({enCours.length})</TabsTrigger>
            <TabsTrigger value="planifie">Planifiées ({planifiees.length})</TabsTrigger>
            <TabsTrigger value="termine">Terminées ({terminees.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {infusions.map((infusion) => (
                <InfusionCard key={infusion.id} infusion={infusion} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="en_cours" className="mt-6">
            {enCours.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Aucune perfusion en cours</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enCours.map((infusion) => (
                  <InfusionCard key={infusion.id} infusion={infusion} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="planifie" className="mt-6">
            {planifiees.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Aucune perfusion planifiée</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {planifiees.map((infusion) => (
                  <InfusionCard key={infusion.id} infusion={infusion} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="termine" className="mt-6">
            {terminees.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Aucune perfusion terminée</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {terminees.map((infusion) => (
                  <InfusionCard key={infusion.id} infusion={infusion} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <InfusionFormDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingInfusion(null);
        }}
        onSuccess={handleInfusionCreated}
        infusion={editingInfusion}
      />

      {monitoringInfusion && (
        <InfusionMonitor
          open={!!monitoringInfusion}
          onClose={() => setMonitoringInfusion(null)}
          infusion={monitoringInfusion}
          onUpdate={handleMonitorUpdate}
        />
      )}
    </div>
  );
};

export default Infusions;
